<template>
  <div class="container py-4">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
      <div>
        <h2 class="mb-0">
          <i class="bi bi-journal-richtext me-2 text-success"></i>Recetas
        </h2>
        <small class="text-muted">Catálogo Metamorfosis y recetas que generas tú</small>
      </div>
      <span v-if="!cargando" class="badge bg-secondary">
        {{ pagination.total }} {{ pagination.total === 1 ? 'receta' : 'recetas' }}
      </span>
    </div>

    <RecetaGenerar
      :llm-configured="generacion.llmConfigured"
      :tipos="generacion.tipos"
      @guardada="onRecetaGuardada"
    />

    <!-- Buscador predictivo -->
    <div class="recetas-buscador mb-3" ref="buscadorWrap">
      <div class="input-group">
        <span class="input-group-text bg-white">
          <i class="bi bi-search"></i>
        </span>
        <input
          type="text"
          class="form-control"
          placeholder="Buscar receta (ej: salmón, huevos, poke...)"
          v-model="textoBusqueda"
          @input="onInputBusqueda"
          @keydown="onKeydownBusqueda"
          @focus="mostrarSugerenciasSiHay"
          autocomplete="off"
          aria-autocomplete="list"
          aria-controls="recetas-sugerencias"
          :aria-expanded="panelSugerenciasVisible.toString()"
        />
        <button
          v-if="textoBusqueda"
          class="btn btn-outline-secondary"
          type="button"
          @click="limpiarBusqueda"
          title="Limpiar"
        >
          <i class="bi bi-x-lg"></i>
        </button>
        <button class="btn btn-success" type="button" @click="aplicarBusqueda" :disabled="cargando">
          <i class="bi bi-search me-1"></i>Buscar
        </button>
      </div>

      <ul
        v-if="panelSugerenciasVisible"
        id="recetas-sugerencias"
        class="list-group recetas-sugerencias shadow"
        role="listbox"
      >
        <li
          v-for="(sug, idx) in sugerencias"
          :key="`${sug.kind}-${sug.id}`"
          class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
          :class="{ active: idx === indiceActivo }"
          role="option"
          :aria-selected="idx === indiceActivo"
          @mousedown.prevent="seleccionarSugerencia(sug)"
        >
          <span>
            <i class="bi bi-egg-fried me-2 opacity-75"></i>{{ sug.titulo }}
          </span>
          <span class="badge rounded-pill" :class="badgeFuente(sug.kind)">
            {{ sug.fuente }}
          </span>
        </li>
        <li v-if="sugerencias.length === 0 && textoBusqueda.trim()" class="list-group-item text-muted small">
          Sin coincidencias para «{{ textoBusqueda.trim() }}»
        </li>
      </ul>
    </div>

    <div class="d-flex flex-wrap gap-2 mb-4">
      <button
        v-for="opcion in filtros"
        :key="opcion.id"
        type="button"
        class="btn btn-sm"
        :class="filtro === opcion.id ? 'btn-success' : 'btn-outline-secondary'"
        @click="cambiarFiltro(opcion.id)"
      >
        {{ opcion.label }}
        <span v-if="opcion.id === 'mias' && propiasCount" class="badge bg-light text-success ms-1">
          {{ propiasCount }}
        </span>
      </button>
    </div>

    <div v-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
    </div>

    <div v-if="cargando && recetas.length === 0" class="text-center py-5">
      <div class="spinner-border text-success" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="text-muted mt-2">Cargando recetas...</p>
    </div>

    <div v-else-if="!cargando && recetas.length === 0" class="text-center py-5 text-muted">
      <i class="bi bi-journal-x fs-1 d-block mb-2"></i>
      <p class="mb-0">
        No se encontraron recetas{{ queryActiva ? ` para «${queryActiva}»` : '' }}.
      </p>
      <button v-if="queryActiva" class="btn btn-link mt-2" @click="limpiarBusqueda">Ver todas</button>
    </div>

    <div v-else class="row g-3">
      <div
        v-for="receta in recetas"
        :key="`${receta.kind}-${receta.id}`"
        class="col-12 col-sm-6 col-lg-4"
      >
        <button
          v-if="receta.kind === 'usuario'"
          type="button"
          class="receta-card receta-card-btn text-start d-block h-100 w-100"
          @click="abrirPropia(receta)"
        >
          <div class="border rounded p-3 h-100 receta-card-inner">
            <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
              <span class="badge rounded-pill bg-success">{{ receta.fuente }}</span>
              <i class="bi bi-journal-check text-success small"></i>
            </div>
            <h3 class="h6 mb-1 text-dark">{{ receta.titulo }}</h3>
            <p v-if="metaReceta(receta)" class="small text-muted mb-0">{{ metaReceta(receta) }}</p>
          </div>
        </button>
        <a
          v-else
          :href="receta.href"
          target="_blank"
          rel="noopener noreferrer"
          class="receta-card text-decoration-none d-block h-100"
        >
          <div class="border rounded p-3 h-100 receta-card-inner">
            <div class="d-flex justify-content-between align-items-start gap-2 mb-2">
              <span class="badge rounded-pill" :class="badgeFuente(receta.kind)">
                {{ receta.fuente }}
              </span>
              <i class="bi bi-box-arrow-up-right text-muted small"></i>
            </div>
            <h3 class="h6 mb-0 text-dark">{{ receta.titulo }}</h3>
          </div>
        </a>
      </div>
    </div>

    <nav v-if="pagination.totalPages > 1" class="mt-4" aria-label="Paginación de recetas">
      <ul class="pagination justify-content-center pagination-sm flex-wrap mb-0">
        <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="cambiarPagina(pagination.currentPage - 1)">
            Anterior
          </a>
        </li>
        <li
          v-for="pagina in paginasVisibles"
          :key="pagina"
          class="page-item"
          :class="{ active: pagination.currentPage === pagina }"
        >
          <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina)">{{ pagina }}</a>
        </li>
        <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
          <a class="page-link" href="#" @click.prevent="cambiarPagina(pagination.currentPage + 1)">
            Siguiente
          </a>
        </li>
      </ul>
      <p class="text-center text-muted small mt-2 mb-0">
        Página {{ pagination.currentPage }} de {{ pagination.totalPages }}
      </p>
    </nav>

    <RecetaDetalle
      v-if="detalle"
      :receta="detalle"
      @cerrar="onCerrarDetalle"
      @eliminada="onRecetaEliminada"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '../config/axios';
import RecetaGenerar from '../components/RecetaGenerar.vue';
import RecetaDetalle from '../components/RecetaDetalle.vue';
import { etiquetaTipo, idRecetaMiaDesdeQuery } from '../utils/recetasForm';

const LIMIT = 12;
const FILTROS = [
  { id: 'todas', label: 'Todas' },
  { id: 'mias', label: 'Mías' },
  { id: 'catalogo', label: 'Catálogo' },
];

function badgeFuente(kind) {
  if (kind === 'meta') return 'bg-primary';
  if (kind === 'usuario') return 'bg-success';
  return 'bg-info text-dark';
}

function metaReceta(receta) {
  const partes = [];
  if (receta.tipo) {
    partes.push(etiquetaTipo(receta.tipo));
  }
  if (receta.raciones) partes.push(`${receta.raciones} raciones`);
  if (receta.tiempo_min) partes.push(`${receta.tiempo_min} min`);
  return partes.join(' · ');
}

export default {
  name: 'Recetas',
  components: { RecetaGenerar, RecetaDetalle },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const recetas = ref([]);
    const sugerencias = ref([]);
    const textoBusqueda = ref('');
    const queryActiva = ref('');
    const filtro = ref('todas');
    const propiasCount = ref(0);
    const cargando = ref(false);
    const error = ref('');
    const panelAbierto = ref(false);
    const indiceActivo = ref(-1);
    const buscadorWrap = ref(null);
    const detalle = ref(null);
    const generacion = ref({
      llmConfigured: false,
      tipos: ['desayuno', 'comida', 'cena', 'snack', 'guarnicion'],
    });
    let debounceTimer = null;

    const pagination = ref({
      total: 0,
      totalPages: 1,
      currentPage: 1,
      limit: LIMIT,
    });

    const panelSugerenciasVisible = computed(
      () => panelAbierto.value && textoBusqueda.value.trim().length > 0
    );

    const paginasVisibles = computed(() => {
      const total = pagination.value.totalPages;
      const actual = pagination.value.currentPage;
      const max = 5;
      let inicio = Math.max(1, actual - Math.floor(max / 2));
      let fin = Math.min(total, inicio + max - 1);
      if (fin - inicio + 1 < max) {
        inicio = Math.max(1, fin - max + 1);
      }
      const pags = [];
      for (let i = inicio; i <= fin; i++) pags.push(i);
      return pags;
    });

    const cargarRecetas = async (page = 1, q = queryActiva.value) => {
      cargando.value = true;
      error.value = '';
      try {
        const response = await axios.get('/api/recetas', {
          params: { page, limit: LIMIT, q: q || undefined, filtro: filtro.value },
        });
        recetas.value = response.data.recetas || [];
        if (response.data.pagination) {
          pagination.value = { ...pagination.value, ...response.data.pagination };
        }
        queryActiva.value = response.data.query || '';
        propiasCount.value = response.data.propias || 0;
        if (response.data.generacion) {
          generacion.value = { ...generacion.value, ...response.data.generacion };
        }
      } catch (err) {
        console.error('Error cargando recetas:', err);
        error.value = err.response?.data?.error || 'No se pudieron cargar las recetas';
        recetas.value = [];
      } finally {
        cargando.value = false;
      }
    };

    const cargarSugerencias = async (q) => {
      const termino = (q || '').trim();
      if (!termino) {
        sugerencias.value = [];
        return;
      }
      try {
        const response = await axios.get('/api/recetas/sugerencias', {
          params: { q: termino, limit: 8 },
        });
        sugerencias.value = response.data.sugerencias || [];
        indiceActivo.value = sugerencias.value.length > 0 ? 0 : -1;
      } catch (err) {
        console.error('Error sugerencias:', err);
        sugerencias.value = [];
      }
    };

    const onInputBusqueda = () => {
      panelAbierto.value = true;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const termino = textoBusqueda.value.trim();
        cargarSugerencias(termino);
        queryActiva.value = termino;
        cargarRecetas(1, termino);
      }, 220);
    };

    const mostrarSugerenciasSiHay = () => {
      if (textoBusqueda.value.trim()) {
        panelAbierto.value = true;
        if (sugerencias.value.length === 0) {
          cargarSugerencias(textoBusqueda.value);
        }
      }
    };

    const aplicarBusqueda = () => {
      clearTimeout(debounceTimer);
      panelAbierto.value = false;
      const termino = textoBusqueda.value.trim();
      queryActiva.value = termino;
      cargarRecetas(1, termino);
    };

    const abrirPropia = async (receta) => {
      try {
        const { data } = await axios.get(`/api/recetas/usuario/${receta.id}`);
        detalle.value = data;
      } catch (err) {
        error.value = err.response?.data?.error || 'No se pudo abrir la receta';
      }
    };

    const limpiarQueryMia = () => {
      if (!idRecetaMiaDesdeQuery(route.query)) return;
      const query = { ...route.query };
      delete query.mia;
      router.replace({ path: '/recetas', query });
    };

    const abrirDesdeQuery = async () => {
      const mia = idRecetaMiaDesdeQuery(route.query);
      if (!mia) return;
      await abrirPropia({ id: mia });
    };

    const seleccionarSugerencia = (sug) => {
      panelAbierto.value = false;
      if (sug.kind === 'usuario') {
        textoBusqueda.value = sug.titulo;
        queryActiva.value = sug.titulo;
        abrirPropia(sug);
        return;
      }
      textoBusqueda.value = sug.titulo;
      queryActiva.value = sug.titulo;
      cargarRecetas(1, sug.titulo);
    };

    const limpiarBusqueda = () => {
      clearTimeout(debounceTimer);
      textoBusqueda.value = '';
      queryActiva.value = '';
      sugerencias.value = [];
      panelAbierto.value = false;
      indiceActivo.value = -1;
      cargarRecetas(1, '');
    };

    const cambiarFiltro = (id) => {
      filtro.value = id;
      cargarRecetas(1, queryActiva.value);
    };

    const cambiarPagina = (pagina) => {
      if (pagina < 1 || pagina > pagination.value.totalPages) return;
      cargarRecetas(pagina, queryActiva.value);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const onKeydownBusqueda = (e) => {
      if (!panelSugerenciasVisible.value) {
        if (e.key === 'Enter') {
          e.preventDefault();
          aplicarBusqueda();
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (sugerencias.value.length === 0) return;
        indiceActivo.value = (indiceActivo.value + 1) % sugerencias.value.length;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (sugerencias.value.length === 0) return;
        indiceActivo.value =
          (indiceActivo.value - 1 + sugerencias.value.length) % sugerencias.value.length;
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (indiceActivo.value >= 0 && sugerencias.value[indiceActivo.value]) {
          seleccionarSugerencia(sugerencias.value[indiceActivo.value]);
        } else {
          aplicarBusqueda();
        }
      } else if (e.key === 'Escape') {
        panelAbierto.value = false;
      }
    };

    const onClickOutside = (e) => {
      if (buscadorWrap.value && !buscadorWrap.value.contains(e.target)) {
        panelAbierto.value = false;
      }
    };

    const onCerrarDetalle = () => {
      detalle.value = null;
      limpiarQueryMia();
    };

    const onKeydownGlobal = (e) => {
      if (e.key === 'Escape' && detalle.value) {
        onCerrarDetalle();
      }
    };

    const onRecetaGuardada = (receta) => {
      filtro.value = 'mias';
      cargarRecetas(1, '');
      if (receta?.id) {
        detalle.value = receta;
      }
    };

    const onRecetaEliminada = () => {
      detalle.value = null;
      limpiarQueryMia();
      cargarRecetas(pagination.value.currentPage, queryActiva.value);
    };

    watch(() => route.query.mia, () => {
      abrirDesdeQuery();
    });

    onMounted(() => {
      document.addEventListener('click', onClickOutside);
      document.addEventListener('keydown', onKeydownGlobal);
      cargarRecetas(1, '');
      abrirDesdeQuery();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', onClickOutside);
      document.removeEventListener('keydown', onKeydownGlobal);
      clearTimeout(debounceTimer);
    });

    return {
      recetas,
      sugerencias,
      textoBusqueda,
      queryActiva,
      filtro,
      filtros: FILTROS,
      propiasCount,
      cargando,
      error,
      pagination,
      panelSugerenciasVisible,
      indiceActivo,
      buscadorWrap,
      paginasVisibles,
      detalle,
      generacion,
      badgeFuente,
      metaReceta,
      onInputBusqueda,
      onKeydownBusqueda,
      mostrarSugerenciasSiHay,
      aplicarBusqueda,
      seleccionarSugerencia,
      limpiarBusqueda,
      cambiarPagina,
      cambiarFiltro,
      abrirPropia,
      onCerrarDetalle,
      onRecetaGuardada,
      onRecetaEliminada,
    };
  },
};
</script>

<style scoped>
.recetas-buscador {
  position: relative;
  max-width: 720px;
}

.recetas-sugerencias {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 2px);
  max-height: 320px;
  overflow-y: auto;
  border-radius: 0.375rem;
}

.recetas-sugerencias .list-group-item.active {
  background-color: #198754;
  border-color: #198754;
}

.receta-card-inner {
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  background: #fff;
}

.receta-card:hover .receta-card-inner {
  border-color: #198754 !important;
  box-shadow: 0 0.25rem 0.75rem rgba(25, 135, 84, 0.12);
  transform: translateY(-1px);
}

.receta-card h3 {
  line-height: 1.35;
}

.receta-card-btn {
  background: none;
  border: 0;
  padding: 0;
}
</style>
